<template>
  <PremiumFeature feature="custom_roles">
    <Dialog v-model:open="open">
      <DialogTrigger as-child>
        <button
          class="flex items-center gap-2 p-3 text-neutral-500 hover:text-neutral-100"
        >
          <Plus :size="16" />
          New workspace role
        </button>
      </DialogTrigger>

      <DialogContent>
        <DialogHeader>
          <DialogTitle>Create new role</DialogTitle>
        </DialogHeader>
        <div class="px-7 pb-6 pt-5 w-full md:max-w-[450px]">
          <form id="new-role" class="space-y-5" @submit="onSubmit">
            <DialogDescription class="text-sm font-semibold">
              Create a custom role which can be assigned to your workspace
              members.
            </DialogDescription>
            <fieldset class="flex flex-col gap-1">
              <Label for="name">Name</Label>
              <Input v-bind="nameAttrs" id="name" v-model="name" />
              <span class="text-error-400">{{ errors.name }}</span>
            </fieldset>
            <fieldset class="flex flex-col gap-1">
              <Label for="description">Description</Label>
              <Textarea
                v-bind="descriptionAttrs"
                id="description"
                v-model="description"
              />
              <span class="text-error-400">{{ errors.description }}</span>
            </fieldset>
          </form>
        </div>
        <DialogFooter>
          <DialogClose as-child>
            <Button variant="ghost" :disabled="isPending">Cancel</Button>
          </DialogClose>
          <Button
            type="submit"
            :disabled="isPending"
            :loading="isPending"
            form="new-role"
          >
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
    <template #fallback>
      <UpgradeModal>
        <template #trigger>
          <button
            class="flex items-center gap-2 p-3 text-neutral-500 hover:text-neutral-100"
          >
            <Plus :size="16" />
            New workspace role
          </button>
        </template>
        <template #upgradeMessage>
          <Alert
            title="The custom roles feature is not available to your current plan"
            subtitle="Upgrade to Team plan to unlock custom roles feature"
            class="mb-6"
          />
        </template>
      </UpgradeModal>
    </template>
  </PremiumFeature>
</template>

<script setup>
import { ref } from 'vue';
import { useRouter } from 'vue-router';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogTrigger,
  DialogDescription,
  DialogHeader,
  DialogFooter,
  DialogClose,
} from '@ui/components/dialog';
import { Alert } from '@ui/components/alert';
import { Plus } from 'lucide-vue-next';
import { toTypedSchema } from '@vee-validate/zod';
import { z } from 'zod';
import { useForm } from 'vee-validate';
import { useMutation, useQueryClient } from '@tanstack/vue-query';

import PremiumFeature from '@hoc/PremiumFeature.vue';
import { UpgradeModal } from '@components/common/UpgradeModal';

import requests from '@vuex/requests.js';
import { Input } from '@ui/components/input';
import { Textarea } from '@ui/components/textarea';
import { Label } from '@ui/components/label';
import { Button } from '@ui/components/button';
import { queries } from '@/queries/index.js';
import { useStore } from 'vuex';

const validationSchema = toTypedSchema(
  z.object({
    name: z
      .string()
      .min(1, {
        message: 'The name field is required',
      })
      .max(255),
    description: z
      .string()
      .min(1, {
        message: 'The description field is required',
      })
      .max(255),
  })
);

const open = ref(false);
const router = useRouter();
const { handleSubmit, errors, defineField, resetForm } = useForm({
  validationSchema,
});

const queryClient = useQueryClient();

const [name, nameAttrs] = defineField('name');
const [description, descriptionAttrs] = defineField('description');

const store = useStore();

const { isPending, mutate } = useMutation({
  mutationFn: async payload => {
    const role = await requests.roles.create(
      store.getters['workspace/getId'],
      payload
    );
    return role;
  },
  onSuccess: async data => {
    await queryClient.invalidateQueries({
      queryKey: queries.workspace.roles.queryKey,
    });
    open.value = false;
    resetForm();
    router.push({
      name: 'workspace-role',
      params: {
        role: data.id,
      },
    });
  },
});

const onSubmit = handleSubmit(values => mutate(values));
</script>
